import { Copy, Pencil, RotateCcw, Trash2, Gift } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/schema";
import { EmptyState } from "@/shared/components/EmptyState";
import { MarketplaceChip, type Marketplace } from "@/shared/components/MarketplaceChip";
import { RowActionMenu, type RowAction } from "@/shared/components/RowActionMenu";
import { Skeleton } from "@/shared/ui/skeleton";

import { formatPeriod } from "../lib/format-period";
import {
  groupLinkedDrafts,
  isLinkedDraftGroupRow,
  type PromotionRow,
} from "../lib/group-linked-drafts";

import { ActivateToggle } from "./ActivateToggle";
import { CreatedBy } from "./CreatedBy";
import { PromotionStatusBadge } from "./PromotionStatusBadge";
import { QuotaProgressBar } from "./QuotaProgressBar";

type Promotion = components["schemas"]["Promotion"];

interface PromotionTableProps {
  items: Promotion[];
  isLoading: boolean;
  isError?: boolean;
  /** Selection is optional — the campaign-detail listing renders without it. */
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onToggleSelectAll?: (ids: string[]) => void;
  /** Wires the per-row Activate switch when present (hidden on campaign-detail). */
  onActivateToggle?: (p: Promotion) => void;
  /** Promotion whose transition mutation is in flight (toggle shows disabled). */
  pendingActivateId?: string | null;
  onEdit?: (p: Promotion) => void;
  onClone?: (p: Promotion) => void;
  /** Reopen a finished promotion back to draft (only fires when quota_used === 0). */
  onReopen?: (p: Promotion) => void;
  onDelete?: (p: Promotion) => void;
  /** Show the Campaign column. Hidden when listing inside a single campaign. */
  showCampaign?: boolean;
  /** promotions:manage — when false the row action menu is not rendered (name link still opens the builder). */
  canManage?: boolean;
}

const TH =
  "h-9 px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap select-none";
const TD = "px-4 py-3 text-sm text-[var(--text-primary)] align-middle";

export function PromotionTable({
  items,
  isLoading,
  isError,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onActivateToggle,
  pendingActivateId,
  onEdit,
  onClone,
  onReopen,
  onDelete,
  showCampaign = true,
  canManage = true,
}: PromotionTableProps): JSX.Element {
  const { t } = useTranslation("promotion");
  const selectable = Boolean(onToggleSelect);
  const activatable = Boolean(onActivateToggle);
  const rows = groupLinkedDrafts(items);
  // Collapsed group rows render no checkbox (ADR-0021: grouped drafts must go
  // through the dedicated ActivateLinkedGroup all-or-nothing path, never the
  // ordinary bulk-transition endpoint). "Select all" must only ever select
  // promotions that have a visible, individually-selectable checkbox.
  const hiddenInCollapsedGroup = new Set(
    rows.flatMap((row) => (isLinkedDraftGroupRow(row) ? row.members.map((m) => m.id) : [])),
  );
  const selectableIds = items.filter((p) => !hiddenInCollapsedGroup.has(p.id)).map((p) => p.id);
  const allSelected =
    selectable && selectableIds.length > 0 && selectableIds.every((id) => selectedIds?.has(id));

  if (isError || (!isLoading && items.length === 0)) {
    return (
      <div
        className="rounded-2xl border border-[var(--premium-border)] bg-white"
        data-testid="promotion-table"
      >
        <EmptyState
          icon={<Gift className="h-5 w-5" aria-hidden />}
          title={t("empty.title")}
          body={t("empty.body")}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className="space-y-2 rounded-2xl border border-[var(--premium-border)] bg-white p-4"
        data-testid="promotion-table-loading"
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-2xl border border-[var(--premium-border)] bg-white">
      <table className="w-full border-collapse" data-testid="promotion-table">
        <thead>
          <tr className="border-b border-[var(--border-subtle)]">
            {selectable && (
              <th className={`${TH} w-10`}>
                <input
                  type="checkbox"
                  aria-label={t("bulk.activate")}
                  data-testid="promotion-select-all"
                  checked={allSelected}
                  onChange={() => onToggleSelectAll?.(selectableIds)}
                />
              </th>
            )}
            {activatable && <th className={`${TH} w-[72px]`}>{t("column.activate")}</th>}
            <th className={TH}>{t("column.status")}</th>
            <th className={TH}>{t("column.name")}</th>
            {showCampaign && <th className={TH}>{t("detail.field.campaign")}</th>}
            <th className={TH}>{t("detail.field.marketplace")}</th>
            <th className={TH}>{t("column.seller")}</th>
            <th className={TH}>{t("column.period")}</th>
            <th className={TH}>{t("column.quota")}</th>
            <th className={`${TH} min-w-[180px]`}>{t("column.created_by")}</th>
            <th className={`${TH} text-right`}>{t("column.note")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row: PromotionRow) => {
            if (isLinkedDraftGroupRow(row)) {
              const marketplaces = row.members.map((m) => m.marketplace as Marketplace);
              return (
                <tr
                  key={row.groupId}
                  data-testid="promotion-row"
                  className="border-b border-[var(--border-subtle)] last:border-0 hover:bg-[var(--canvas)]"
                >
                  {selectable && <td className={TD} />}
                  {activatable && <td className={TD} />}
                  <td className={TD}>
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-[var(--intent-neutral-bg)] px-2 py-0.5 text-[11px] font-semibold text-[var(--intent-neutral-fg)]"
                      data-testid="promotion-linked-group-badge"
                    >
                      {t("column.draft_group", { count: row.members.length })}
                    </span>
                  </td>
                  <td className={`${TD} font-semibold`}>
                    {onEdit ? (
                      <button
                        type="button"
                        data-testid="promotion-group-name-link"
                        onClick={() => {
                          const primary = row.members[0];
                          if (primary) onEdit(primary);
                        }}
                        className="font-semibold text-[var(--text-primary)] hover:text-[var(--brand-500)] focus-ring rounded text-left"
                      >
                        {row.members[0]?.name}
                      </button>
                    ) : (
                      row.members[0]?.name
                    )}
                  </td>
                  {showCampaign && (
                    <td className={`${TD} text-[var(--text-secondary)]`}>
                      {row.members[0]?.campaign_name ?? "—"}
                    </td>
                  )}
                  <td className={TD}>
                    <div className="flex flex-wrap gap-1">
                      {marketplaces.map((m) => (
                        <MarketplaceChip key={m} marketplace={m} />
                      ))}
                    </div>
                  </td>
                  <td className={`${TD} text-[var(--text-secondary)]`}>
                    {row.members[0]?.seller_id}
                    {row.members.length > 1 ? ` +${row.members.length - 1} more` : ""}
                  </td>
                  <td className={`${TD} whitespace-nowrap text-[12px] text-[var(--text-subtle)]`}>
                    {formatPeriod(row.members[0]?.start_at ?? "", row.members[0]?.end_at ?? "")}
                  </td>
                  <td className={TD}>—</td>
                  <td className={TD}>
                    <CreatedBy
                      createdBy={row.members[0]?.created_by ?? ""}
                      createdByName={row.members[0]?.created_by_name}
                    />
                  </td>
                  <td className={`${TD} text-right`}>
                    {onEdit && canManage ? (
                      <RowActionMenu
                        actions={[
                          {
                            key: "edit",
                            label: t("action.edit"),
                            icon: <Pencil className="h-4 w-4" aria-hidden />,
                            // Opening any member navigates to the Builder, which reads
                            // linked_group_id and fetches every member via useLinkedGroup —
                            // so it renders the full group regardless of which id anchors it.
                            onSelect: () => {
                              const primary = row.members[0];
                              if (primary) onEdit(primary);
                            },
                          },
                        ]}
                        label={t("action.edit")}
                      />
                    ) : (
                      <span className="text-[var(--text-subtle)]">—</span>
                    )}
                  </td>
                </tr>
              );
            }
            const p = row;
            const actions: RowAction[] = [
              ...(onEdit
                ? [
                    {
                      key: "edit",
                      label: t("action.edit"),
                      icon: <Pencil className="h-4 w-4" aria-hidden />,
                      onSelect: () => onEdit(p),
                    },
                  ]
                : []),
              ...(onClone
                ? [
                    {
                      key: "clone",
                      label: t("action.clone"),
                      icon: <Copy className="h-4 w-4" aria-hidden />,
                      onSelect: () => onClone(p),
                    },
                  ]
                : []),
              // Reopen finish→draft is a recovery action, shown ONLY for finished
              // promotions. It's blocked (dimmed + tooltip) once any redemption has
              // happened (quota_used > 0) — reopening then would corrupt history, so
              // the user is steered to Clone instead.
              ...(onReopen && p.status === "finish"
                ? [
                    {
                      key: "reopen",
                      label: t("action.reopen"),
                      icon: <RotateCcw className="h-4 w-4" aria-hidden />,
                      disabled: p.quota_used > 0,
                      tooltip:
                        p.quota_used > 0
                          ? t("action.reopen_blocked_tooltip")
                          : t("action.reopen_hint"),
                      onSelect: () => onReopen(p),
                    },
                  ]
                : []),
              ...(onDelete
                ? [
                    {
                      key: "delete",
                      label: t("action.delete"),
                      icon: <Trash2 className="h-4 w-4" aria-hidden />,
                      danger: true,
                      onSelect: () => onDelete(p),
                    },
                  ]
                : []),
            ];
            return (
              <tr
                key={p.id}
                data-testid="promotion-row"
                data-selected={selectedIds?.has(p.id) ? "true" : "false"}
                className="border-b border-[var(--border-subtle)] last:border-0 hover:bg-[var(--canvas)]"
              >
                {selectable && (
                  <td className={TD}>
                    <input
                      type="checkbox"
                      aria-label={p.name}
                      data-testid="promotion-select"
                      checked={selectedIds?.has(p.id) ?? false}
                      onChange={() => onToggleSelect?.(p.id)}
                    />
                  </td>
                )}
                {activatable && (
                  <td className={TD}>
                    <ActivateToggle
                      on={p.status === "active"}
                      locked={p.status === "finish" || p.status === "archived"}
                      disabled={pendingActivateId === p.id}
                      label={
                        p.status === "finish" || p.status === "archived"
                          ? t("activate.locked")
                          : t("activate.label", { name: p.name })
                      }
                      onToggle={() => onActivateToggle?.(p)}
                    />
                  </td>
                )}
                <td className={TD}>
                  <PromotionStatusBadge status={p.status} />
                </td>
                <td className={`${TD} font-semibold`}>
                  {onEdit ? (
                    <button
                      type="button"
                      data-testid="promotion-name-link"
                      onClick={() => onEdit(p)}
                      className="font-semibold text-[var(--text-primary)] hover:text-[var(--brand-500)] focus-ring rounded text-left"
                    >
                      {p.name}
                    </button>
                  ) : (
                    p.name
                  )}
                </td>
                {showCampaign && (
                  <td className={`${TD} text-[var(--text-secondary)]`}>{p.campaign_name ?? "—"}</td>
                )}
                <td className={TD}>
                  <MarketplaceChip marketplace={p.marketplace as Marketplace} />
                </td>
                <td className={`${TD} text-[var(--text-secondary)]`}>{p.seller_id}</td>
                <td className={`${TD} whitespace-nowrap text-[12px] text-[var(--text-subtle)]`}>
                  {formatPeriod(p.start_at, p.end_at)}
                </td>
                <td className={TD}>
                  <QuotaProgressBar used={p.quota_used} total={p.quota_total} />
                </td>
                <td className={TD}>
                  <CreatedBy createdBy={p.created_by} createdByName={p.created_by_name} />
                </td>
                <td className={`${TD} text-right`}>
                  {canManage && actions.length > 0 ? (
                    <RowActionMenu actions={actions} label={t("action.edit")} />
                  ) : (
                    <span className="text-[var(--text-subtle)]">{p.note ? "ⓘ" : "—"}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
